import { z } from 'zod'

/**
 * Authentication contract.
 *
 * Two ways in:
 *
 *   - E-MAIL, PASSWORD AND A TOTP CODE — the normal one. Two steps, both
 *     required: the password alone buys a five-minute single-use ticket, never
 *     a session, and only the code completes the sign-in. Every account has
 *     this, and enrolment sets it up.
 *   - A PASSKEY, optional, for anyone who wants one. A single step, because it
 *     is already two factors — the device, plus the fingerprint or face that
 *     unlocks it (`userVerification: 'required'`) — and phishing-resistant,
 *     because the browser will not release it to any origin but this one.
 *     Nobody is required to have one, so nobody is blocked by an authenticator
 *     that will not cooperate.
 *
 * There is no sign-up. An admin creates the account and hands out a single-use
 * enrolment link.
 */

export const userRoles = ['admin', 'agent'] as const
export const userStatuses = ['invited', 'active', 'disabled'] as const

export const userRoleSchema = z.enum(userRoles)
export const userStatusSchema = z.enum(userStatuses)

export type UserRole = z.infer<typeof userRoleSchema>
export type UserStatus = z.infer<typeof userStatusSchema>

/** German labels, like the rest of the UI. */
export const USER_ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Administrator',
  agent: 'Makler',
}

export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  invited: 'Eingeladen',
  active: 'Aktiv',
  disabled: 'Deaktiviert',
}

/** The signed-in person, as every authenticated page sees them. */
export const sessionUserSchema = z.object({
  id: z.string(),
  email: z.email(),
  name: z.string(),
  role: userRoleSchema,
  /** How the current session was established — see the note above. */
  method: z.enum(['passkey', 'password', 'enrolment']),
  /** False right after a fallback sign-in: the UI nudges them to enrol one. */
  hasPasskey: z.boolean(),
  hasTotp: z.boolean(),
  hasPassword: z.boolean(),
})

export type SessionUser = z.infer<typeof sessionUserSchema>

export const meResponseSchema = z.object({
  user: sessionUserSchema.nullable(),
  /**
   * Whether this server can actually send e-mail. Without SMTP every mail is
   * only written to the server log, and the login page must not offer a
   * "Passwort vergessen?" form that promises a message nobody will receive —
   * it points to an administrator instead.
   */
  mail: z.object({ configured: z.boolean() }),
})

export type MeResponse = z.infer<typeof meResponseSchema>

/* ------------------------------------------------------------- ceremony --- */

/**
 * WebAuthn option and response payloads are defined by the spec and produced by
 * the browser, so they are passed through as opaque JSON rather than re-modelled
 * here — a stricter schema would only reject valid ceremonies the day a browser
 * adds a field.
 */
const webauthnPayload = z.looseObject({})

export const publicKeyOptionsResponseSchema = z.object({
  options: webauthnPayload,
})

export type PublicKeyOptionsResponse = z.infer<typeof publicKeyOptionsResponseSchema>

export const passkeyLoginStartSchema = z.object({
  /**
   * Optional. Modern authenticators store a discoverable credential, so the
   * browser can offer the right passkey without being told who is signing in —
   * and not asking means the login page never reveals which addresses exist.
   */
  email: z.email().optional(),
})

export const passkeyVerifySchema = z.object({
  credential: webauthnPayload,
  /** Free-text device name, only used when registering. */
  label: z.string().max(80).optional(),
})

/** Step one of the fallback. Answers with `mfaRequired`, never with a session. */
export const passwordLoginSchema = z.object({
  email: z.email(),
  password: z.string().min(1).max(200),
})

export const mfaRequiredSchema = z.object({
  mfaRequired: z.literal(true),
})

/**
 * Step two. No e-mail: the account is named by the single-use ticket cookie
 * from step one, so this endpoint cannot be pointed at an account whose
 * password was never given.
 */
export const totpLoginSchema = z.object({
  /** Six digits from the authenticator app. */
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Bitte sechs Ziffern eingeben'),
})

export const loginResponseSchema = z.object({
  user: sessionUserSchema,
})

export type LoginResponse = z.infer<typeof loginResponseSchema>

/* ------------------------------------------------------------ enrolment --- */

/** What the enrolment page shows before anybody touches an authenticator. */
export const inviteInfoSchema = z.object({
  email: z.email(),
  name: z.string(),
  role: userRoleSchema,
  expiresAt: z.iso.datetime(),
  /**
   * Enrolment is resumable — a closed tab must not mean starting over, since
   * the invite is single-use and only consumed at the very end.
   */
  passwordSet: z.boolean(),
  /**
   * True when the account was already active — an admin re-invited somebody
   * who lost their authenticator. The page then says "Zugang neu einrichten"
   * rather than welcoming a newcomer, and the server signs every other device
   * out when it completes.
   */
  reenrolment: z.boolean(),
})

export type InviteInfo = z.infer<typeof inviteInfoSchema>

/**
 * The TOTP secret, handed over exactly once during enrolment. The QR is a data
 * URL so the page needs no image endpoint and the secret never lands in a URL a
 * proxy might log.
 */
export const totpSetupSchema = z.object({
  secret: z.string(),
  otpauthUrl: z.string(),
  qrDataUrl: z.string(),
})

export type TotpSetup = z.infer<typeof totpSetupSchema>

export const totpConfirmSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Bitte sechs Ziffern eingeben'),
})

/* -------------------------------------------------- password rules ------- */

/**
 * The password rules live HERE, not in the API, so the checklist the person
 * watches while typing and the check that decides whether to store the password
 * are the same function. Two copies would drift, and the way they drift is a
 * form that says every requirement is met and a server that disagrees.
 */

export const PASSWORD_MIN_LENGTH = 8

/**
 * Argon2 reads the whole input, so an unbounded one is a memory-and-CPU DoS
 * against an UNAUTHENTICATED route. The rate limiter bounds the number of
 * attempts; this bounds the cost of each.
 */
export const PASSWORD_MAX_LENGTH = 200

export type PasswordSubject = { email: string; name: string }

export type PasswordRule = {
  id: string
  /** Positive phrasing, for the live checklist. */
  label: string
  /** What the server says when it refuses. */
  problem: string
  /** Shown in the checklist as something to aim for, rather than only on breach. */
  listed: boolean
  ok: (password: string, who: PasswordSubject) => boolean
}

/** Longest run of the subject's own words that a password may not contain. */
const IDENTIFYING_PART_MIN = 3

function identifyingParts(who: PasswordSubject): string[] {
  const local = who.email.split('@')[0]?.toLowerCase() ?? ''
  return [local, ...who.name.toLowerCase().split(/\s+/)].filter(
    (part) => part.length >= IDENTIFYING_PART_MIN,
  )
}

/**
 * Length over composition classes. NIST dropped the "one uppercase, one digit,
 * one symbol" rules because they push people to `Sommer2024!` — predictable to
 * a cracker and annoying to a human. The real second factor on this path is the
 * TOTP code; this is the half that only has to resist being guessed by somebody
 * who already knows whose account it is.
 */
export const passwordRules: PasswordRule[] = [
  {
    id: 'length',
    label: `Mindestens ${PASSWORD_MIN_LENGTH} Zeichen`,
    problem: `Das Passwort muss mindestens ${PASSWORD_MIN_LENGTH} Zeichen lang sein.`,
    listed: true,
    ok: (password) => password.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: 'identity',
    label: 'Enthält nicht den eigenen Namen oder die E-Mail-Adresse',
    problem: 'Das Passwort darf den eigenen Namen oder die E-Mail-Adresse nicht enthalten.',
    listed: true,
    ok: (password, who) => {
      const lowered = password.toLowerCase()
      // The two strings somebody who already knows the account tries first.
      return !identifyingParts(who).some((part) => lowered.includes(part))
    },
  },
  {
    id: 'not-blank',
    label: 'Besteht nicht nur aus Leerzeichen',
    problem: 'Das Passwort darf nicht nur aus Leerzeichen bestehen.',
    // Unlisted: nobody needs telling this up front, and it would read as though
    // we expected them to try.
    listed: false,
    ok: (password) => password.trim().length > 0,
  },
  {
    id: 'max-length',
    label: `Höchstens ${PASSWORD_MAX_LENGTH} Zeichen`,
    problem: `Das Passwort darf höchstens ${PASSWORD_MAX_LENGTH} Zeichen lang sein.`,
    listed: false,
    ok: (password) => password.length <= PASSWORD_MAX_LENGTH,
  },
]

export type PasswordRuleState = { id: string; label: string; ok: boolean }

/** For the live checklist: every LISTED rule and whether it is currently met. */
export function passwordRuleStates(password: string, who: PasswordSubject): PasswordRuleState[] {
  return passwordRules
    .filter((rule) => rule.listed)
    .map((rule) => ({ id: rule.id, label: rule.label, ok: rule.ok(password, who) }))
}

/** For the server: the German sentences for every rule that is not met. */
export function passwordProblems(password: string, who: PasswordSubject): string[] {
  return passwordRules.filter((rule) => !rule.ok(password, who)).map((rule) => rule.problem)
}

export function isPasswordAcceptable(password: string, who: PasswordSubject): boolean {
  return passwordRules.every((rule) => rule.ok(password, who))
}

export const passwordRuleText =
  'Eine Passphrase aus mehreren Wörtern ist einfacher zu merken und sicherer als ein kurzes Passwort mit Sonderzeichen.'

/*
 * Length is bounded here only to cap the hashing cost — the readable rules
 * above are what produce the message. A `.min()` here would short-circuit them
 * and hand the person a generic validation error instead.
 */
export const setPasswordSchema = z.object({
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
})

export const changePasswordSchema = z.object({
  /** Proves it is the account holder at the keyboard, not a borrowed session. */
  currentPassword: z.string().min(1).max(PASSWORD_MAX_LENGTH),
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
})

/* ---------------------------------------------------------- credentials --- */

export const passkeySummarySchema = z.object({
  id: z.string(),
  label: z.string().nullable(),
  deviceType: z.string().nullable(),
  backedUp: z.boolean(),
  createdAt: z.iso.datetime(),
  lastUsedAt: z.iso.datetime().nullable(),
})

export type PasskeySummary = z.infer<typeof passkeySummarySchema>

export const passkeyListSchema = z.object({
  passkeys: z.array(passkeySummarySchema),
})

/* -------------------------------------------------------------- sessions --- */

/**
 * One signed-in device. `id` is the SHA-256 of the session token — safe to show
 * its owner, because a hash cannot be presented as a cookie, and it is what
 * they pass back to revoke it.
 */
export const sessionRecordSchema = z.object({
  id: z.string(),
  method: z.enum(['passkey', 'password', 'enrolment']),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  createdAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime(),
  /** The device asking. The UI must not offer to sign it out by accident. */
  current: z.boolean(),
})

export type SessionRecord = z.infer<typeof sessionRecordSchema>

export const sessionListSchema = z.object({
  sessions: z.array(sessionRecordSchema),
})

/* ---------------------------------------------------------------- admin --- */

export const userSummarySchema = z.object({
  id: z.string(),
  email: z.email(),
  name: z.string(),
  role: userRoleSchema,
  status: userStatusSchema,
  passkeyCount: z.number().int().nonnegative(),
  hasTotp: z.boolean(),
  hasPassword: z.boolean(),
  lastLoginAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  /** Set while an unredeemed invite is outstanding. */
  inviteExpiresAt: z.iso.datetime().nullable(),
})

export type UserSummary = z.infer<typeof userSummarySchema>

export const userListSchema = z.object({
  users: z.array(userSummarySchema),
})

export const createUserSchema = z.object({
  email: z.email().transform((value) => value.trim().toLowerCase()),
  name: z.string().trim().min(1).max(120),
  role: userRoleSchema.default('agent'),
})

export const updateUserSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  role: userRoleSchema.optional(),
  /** Only these two are settable: `invited` is a consequence, not a choice. */
  status: z.enum(['active', 'disabled']).optional(),
})

/**
 * The enrolment link, returned once when the invite is created.
 *
 * Mailed to the person as well, but shown here so an admin can hand it over
 * directly — and while no SMTP server is configured, "mailed" means written
 * to the server log. The token is not stored in the clear, so this response
 * is the only chance to read it.
 */
export const inviteResponseSchema = z.object({
  user: userSummarySchema,
  enrolmentUrl: z.string(),
  expiresAt: z.iso.datetime(),
})

export type InviteResponse = z.infer<typeof inviteResponseSchema>

/* ------------------------------------------------------ password reset --- */

/**
 * A reset link changes the PASSWORD AND NOTHING ELSE. The authenticator stays
 * required afterwards, so whoever controls the mailbox still has only one of
 * the two factors. A lost authenticator is an admin's "neu einladen", which
 * starts the whole enrolment over.
 */

/** "Passwort vergessen?" — always answers the same way, whatever the address. */
export const passwordResetRequestSchema = z.object({
  email: z.email().transform((value) => value.trim().toLowerCase()),
})

/**
 * The answer to a request, identical whether the address exists or not. The
 * literal exists so the client parses SOMETHING: an endpoint that answered an
 * empty 200 would be the one place `apiFetch` had nothing to check.
 */
export const passwordResetRequestedSchema = z.object({
  requested: z.literal(true),
})

/** What the reset page shows: whose password this is, and until when. */
export const passwordResetInfoSchema = z.object({
  email: z.email(),
  name: z.string(),
  expiresAt: z.iso.datetime(),
})

export type PasswordResetInfo = z.infer<typeof passwordResetInfoSchema>

/**
 * The reset link, when an ADMIN issues it. Mailed as well, but shown here so it
 * can be handed over directly — the normal case while no mail server exists.
 */
export const passwordResetResponseSchema = z.object({
  user: userSummarySchema,
  resetUrl: z.string(),
  expiresAt: z.iso.datetime(),
})

export type PasswordResetResponse = z.infer<typeof passwordResetResponseSchema>
